
  import { defineComponent } from 'vue';
  import { mapActions, mapGetters } from 'vuex';
  import Multiselect from '@vueform/multiselect';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import text from '@/common/mixins/text';

  import AccessConditions from '@/modules/countryАnalytics/AccessConditions/AccessConditions.vue';
  import Budget from '@/modules/countryАnalytics/components/Budget.vue';
  import CountryRanking from '@/modules/countryАnalytics/components/countryRanking/CountryRanking.vue';
  import EconomicOverview from '@/modules/countryАnalytics/components/EconomicOverview/EconomicOverview.vue';
  import FinancialSector from '@/modules/countryАnalytics/components/FinancialSector/FinancialSector.vue';
  import GreenEconomy from '@/modules/countryАnalytics/components/GreenEconomy.vue';
  import Healthcare from '@/modules/countryАnalytics/components/healthcare/Healthcare.vue';
  import InstitutionalForms from '@/modules/countryАnalytics/components/InstitutionalForms/InstitutionalForms.vue';
  import IntegrationIndicators from '@/modules/countryАnalytics/components/integrationIndicators/IntegrationIndicators.vue';
  import InternationalTrade from '@/modules/countryАnalytics/components/InternationalTrade/InternationalTrade.vue';
  import InternationalTradeRussia from '@/modules/countryАnalytics/components/InternationalTrade/InternationalTradeRussia.vue';
  import Investments from '@/modules/countryАnalytics/components/investments/Investments.vue';
  import LifeIndex from '@/modules/countryАnalytics/components/LifeIndex/lifeIndex.vue';
  import MacroEcon from '@/modules/countryАnalytics/components/MacroEcon/MacroEcon.vue';
  import MapSelectCountry from '@/modules/countryАnalytics/components/MapSelectCountry.vue';
  import MonetaryPolicy from '@/modules/countryАnalytics/components/MonetaryPolicy.vue';
  import Reference from '@/modules/countryАnalytics/components/reference/Reference.vue';
  import ReferenceInfoArchive from '@/modules/countryАnalytics/components/referenceInfo/ReferenceInfoArchive.vue';
  import ReferenceInfoHistory from '@/modules/countryАnalytics/components/referenceInfo/referenceInfoHistory/ReferenceInfoHistory.vue';
  import ReferenceInfoList from '@/modules/countryАnalytics/components/referenceInfo/ReferenceInfoList.vue';
  import ExportPotentialMap from '@/modules/countryАnalytics/ExportPotential/component/ExportPotentialMap.vue';
  import TradeInvestmentMap from '@/modules/countryАnalytics/tradeInvestmentMap/TradeInvestmentMap.vue';
  import ProjectsMap from '@/modules/mpkProject/views/ProjectsMap.vue';

  import RestrictionStatisticTab from '../foreignMarket/components/analytics/statisticTab/RestrictionStatisticTab.vue';
  import ConnectionsWithRegions from './components/connectionsWithRegions/ConnectionsWithRegions.vue';
  import HeaderBlock from './components/HeaderBlock.vue';
  import InvestmentsWithRussia from './components/investments/InvestmentsWithRussia.vue';
  import JointProjects from './components/jointProjects/JointProjects.vue';
  import PaymentBalance from './components/payments/PaymentBalance.vue';
  import PresentationDownloadModal from './components/PresentationDownloadModal.vue';
  import WorldHotIndicator from './components/WorldHotIndicator.vue';
  import { COUNTRY_ANALYTICS_ACTIONS_NAME, COUNTRY_ANALYTICS_GETTERS_NAME } from './store/analytics/directoryNames';
  import type {
    TAnalyticsTab,
    TCountryAnalyticsData,
    TCurrentTab,
    TMapTab,
    TNsiCountry,
  } from './types/countryAnalytics';
  import {
    ActiveTabLabel,
    DataTabs,
    FourthDataTabs,
    ListOfSummonsCountries,
    MapTabs,
    SecondDataTabs,
    SELECT_TYPE_TAB,
    ThirdDataTabs,
  } from './utils/constants';

  import { DIRECTIVES_ACTIONS_NAME, DIRECTIVES_GETTERS_NAME } from '@/store/directives';

  export default defineComponent({
    name: 'CountryAnalytics',
    components: {
      MapSelectCountry,
      WorldHotIndicator,
      ButtonComponent,
      HeaderBlock,
      Multiselect,
      PresentationDownloadModal,
    },
    mixins: [security, text],
    data(): TCountryAnalyticsData {
      return {
        country: Number(this.$route.query?.countryId) > 0 ? String(this.$route.query.countryId) : null,
        selectTypeTab: SELECT_TYPE_TAB,
        countries: [],
        countryNames: [],
        currentCountry: null,
        activeTab: ActiveTabLabel.ECONOMIC_OVERVIEW,
        activeTabId: 1,
        dataTabs: DataTabs,
        tabDataTwo: SecondDataTabs,
        tabDataThree: ThirdDataTabs,
        tabDataFour: FourthDataTabs,
        tabMaps: MapTabs,
        activeTabMap: '',
        activeTabMapId: 1,
        presentation: {
          dialogState: false,
        },
        lifeIndexSummary: null,
        integrationIndicatorsHidden: true,
        lifeIndexHidden: true,
        mapCreateStatuses: [true, false, false, false, false],
        listOfSummonsCountries: ListOfSummonsCountries,
        isUnfriendlyCountry: false,
      };
    },
    computed: {
      ...mapGetters({
        getSummaryLifeIndex: COUNTRY_ANALYTICS_GETTERS_NAME.getSummaryLifeIndex,
        getNsiCountryListWithData: DIRECTIVES_GETTERS_NAME.getNsiCountryListWithData,
      }),
      tabData() {
        if (Number(this.country) === Constants.countryRussiaId) {
          return this.dataTabs.filter(({ text }) => text !== ActiveTabLabel.CONNECTIONS_WITH_REGIONS);
        }
        return this.dataTabs;
      },
      presentationDefaultCountryId() {
        return this.country === String(Constants.countryRussiaId) ? null : this.country;
      },
      secondaryMapTabs() {
        return [
          { id: 2, is: ProjectsMap },
          { id: 3, is: ExportPotentialMap },
          { id: 4, is: TradeInvestmentMap },
          { id: 5, is: RestrictionStatisticTab },
        ];
      },
      currentTab(): Nullable<TCurrentTab> {
        const countryId = this.country;

        if (this.activeTab === ActiveTabLabel.INTERNATIONAL_TRADE) {
          return {
            is: this.selectTypeTab == 1 ? InternationalTrade : InternationalTradeRussia,
            props: { countryId },
          };
        }
        if (this.activeTab === ActiveTabLabel.INVESTMENTS && this.activeTabId == 33) {
          return { is: InvestmentsWithRussia, props: { countryId } };
        }

        const map = {
          [ActiveTabLabel.CREDIT_POLICY]: { is: MonetaryPolicy, props: { countryId } },
          [ActiveTabLabel.COMMON_PROJECTS]: { is: JointProjects, props: { countryId } },
          [ActiveTabLabel.FINANCE]: { is: FinancialSector, props: { countryId } },
          [ActiveTabLabel.CONNECTIONS_WITH_REGIONS]: { is: ConnectionsWithRegions, props: { countryId } },
          [ActiveTabLabel.PAYMENT_BALANCE]: { is: PaymentBalance, props: { currentCountry: this.currentCountry } },
          [ActiveTabLabel.INTEGRATION_INDICATORS]: { is: IntegrationIndicators, props: { countryId } },
          [ActiveTabLabel.HEALTHCARE]: { is: Healthcare, props: { countryId } },
          [ActiveTabLabel.MACROECONOMIC]: { is: MacroEcon, props: { countryId } },
          [ActiveTabLabel.INVESTMENTS]: { is: Investments, props: { countryId } },
          [ActiveTabLabel.ADDITIONAL_INFORMATION]: { is: Reference, props: { countryId } },
          [ActiveTabLabel.RATING]: { is: CountryRanking, props: { countryId } },
          [ActiveTabLabel.LIFE_INDEX]: { is: LifeIndex, props: { indicatorValues: this.lifeIndexSummary } },
          [ActiveTabLabel.FISCAL_POLICY]: { is: Budget, props: { countryId } },
          [ActiveTabLabel.GREEN_ECONOMY]: { is: GreenEconomy, props: { countryId } },
          [ActiveTabLabel.INSTITUTIONAL_COOPERATION]: { is: InstitutionalForms, props: { countryId } },
          [ActiveTabLabel.ACCESS_CONDITIONS]: { is: AccessConditions, props: { countryId }, class: 'accessConditions' },
          [ActiveTabLabel.ECONOMIC_OVERVIEW]: { is: EconomicOverview, props: { countryId } },
          [ActiveTabLabel.REFERENCE_INFORMATION_LIST]: { is: ReferenceInfoList, props: { countryId } },
          [ActiveTabLabel.REFERENCE_INFORMATION_ARCHIVE]: { is: ReferenceInfoArchive, props: { countryId } },
          [ActiveTabLabel.REFERENCE_INFORMATION_HISTORY]: { is: ReferenceInfoHistory, props: { countryId } },
        };

        return map[this.activeTab] ?? null;
      },
    },
    methods: {
      ...mapActions({
        getLifeIndexSummary: COUNTRY_ANALYTICS_ACTIONS_NAME.getLifeIndexSummary,
        getUnfriendlyCountryInfo: COUNTRY_ANALYTICS_ACTIONS_NAME.getUnfriendlyCountryInfo,
        getNsiCountryWithData: DIRECTIVES_ACTIONS_NAME.getNsiCountryWithData,
      }),
      setActiveTypeTab(id: number) {
        this.selectTypeTab = id;
        switch (id) {
          case 1:
            this.selectFirstTab(this.tabData);
            break;
          case 2:
            this.selectFirstTab(this.tabDataTwo);
            break;
          case 3:
            this.selectFirstTab(this.tabDataThree);
            break;
          case 4:
            this.selectFirstTab(this.tabDataFour);
            break;
          default:
            break;
        }
      },
      selectTab(tab: Nullable<TAnalyticsTab>) {
        this.activeTab = tab?.text ?? '';
        this.activeTabId = tab?.id ?? 0;
      },
      selectTabMap(tab: TMapTab) {
        this.activeTabMap = tab.text;
        this.activeTabMapId = tab.id;
        this.mapCreateStatuses[tab.id - 1] = true;
      },
      selectFirstTab(items: TAnalyticsTab[]) {
        const visibleTabs = items.filter((x) => !x.hidden);
        if (visibleTabs && visibleTabs.length > 0) this.selectTab(visibleTabs[0]);
        else this.selectTab(null);
      },
      selectMapCountry({ code, isUnfriendly }: { code: string | number; isUnfriendly: boolean }) {
        this.isUnfriendlyCountry = isUnfriendly;
        this.setActiveTypeTab(3);
        this.country = this.countries.find((c) => c.code === Number(code))?.id ?? null;
        this.currentCountry = this.countries.find((c) => c.id === Number(this.country)) ?? null;
      },
      onCountryFilter() {
        if (this.country === 0) this.country = null;
        if (this.country != null) {
          this.fetchUnfriendlyCountryInfo(this.country);
        }
        this.currentCountry = this.countries.find((c) => c.id === Number(this.country)) ?? null;
        if (
          this.activeTab === ActiveTabLabel.INTEGRATION_INDICATORS &&
          this.currentCountry?.groupId !== Constants.countryGroupEAEC
        ) {
          this.setActiveTypeTab(1);
        }
      },
      onOpenPresentationDialog() {
        this.presentation.dialogState = true;
      },
      setTabVisibility() {
        const permissions = Array(
          Constants.Permissions.CountryAnalyticsEconomicsRussia - Constants.Permissions.CountryAnalyticsMacroEcon + 1,
        )
          .fill(0)
          .map((_: number, idx: number) => Constants.Permissions.CountryAnalyticsMacroEcon + idx);
        this.getAccessList(permissions).then((response) => {
          for (let i = 0; i < this.tabData.length; i++) {
            if (!this.tabData[i].hidden)
              this.tabData[i].hidden = !response.data[i + Constants.Permissions.CountryAnalyticsMacroEcon];
            if (this.tabData[i].id == 2)
              this.integrationIndicatorsHidden = !response.data[i + Constants.Permissions.CountryAnalyticsMacroEcon];
            else if (this.tabData[i].id == 96)
              this.lifeIndexHidden = !response.data[i + Constants.Permissions.CountryAnalyticsMacroEcon];
          }

          for (let i = 0; i < this.tabDataTwo.length; i++) {
            if (!this.tabDataTwo[i].hidden)
              this.tabDataTwo[i].hidden = !response.data[i + Constants.Permissions.CountryAnalyticsRussiaForeignTrade];
          }

          for (let i = 0; i < this.tabDataThree.length; i++) {
            if (!this.tabDataThree[i].hidden)
              this.tabDataThree[i].hidden = !response.data[i + Constants.Permissions.CountryAnalyticsEconomicsReview];
          }
        });

        this.prepareTabDataFour();
      },
      async prepareTabDataFour() {
        this.tabDataFour = await Promise.all(
          this.tabDataFour.map(async (tab) => {
            try {
              const response = await this.checkPermission(tab.permissionId);
              return { ...tab, hidden: !response.data };
            } catch (err) {
              console.error(err);
              return { ...tab, hidden: true };
            }
          }),
        );
      },
      summonCountry(country: string | number) {
        this.country = country;
        this.onCountryFilter();
      },
      changeCountry(country: string | number) {
        this.country = country;
        this.onCountryFilter();
      },

      async loadData() {
        const tab = this.tabData.find((t) => t.id === 96);
        if (tab) tab.hidden = true;
        if (this.country != null && Number(this.country) > 0) {
          this.lifeIndexSummary = await this.fetchLifeIndexSummary(this.country);
          if (this.lifeIndexSummary && tab) tab.hidden = this.lifeIndexHidden;
          else if (this.activeTabId == 96) {
            this.setActiveTypeTab(1);
          }
        }
      },
      async fetchUnfriendlyCountryInfo(countryId: string | number) {
        this.isUnfriendlyCountry = false;

        const res = await this.getUnfriendlyCountryInfo({ id: countryId });

        if (res?.status === 200) {
          this.isUnfriendlyCountry = true;
        }
      },
      async fetchLifeIndexSummary(countryId: string | number) {
        await this.getLifeIndexSummary({ id: countryId });
        return this.getSummaryLifeIndex;
      },
    },
    async created() {
      const countryId = this.$route?.query?.countryId;
      if (countryId) {
        this.country = String(countryId);
        await this.loadData();
      }

      await this.getNsiCountryWithData();

      this.countries = this.getNsiCountryListWithData.map((c: TNsiCountry) => ({
        id: c.id,
        text: c.name,
        fullName: c.nameFull || c.name,
        code: c.code,
        groupId: c.countryGroupId,
      }));

      this.currentCountry = this.countries.find((c) => c.id === Number(this.country)) ?? null;

      this.countryNames = [
        ...this.getNsiCountryListWithData.map((item: TNsiCountry) => {
          return {
            nameFull: item.nameFull || item.name,
            id: item.id,
            code: item.code,
          };
        }),
      ];

      this.setTabVisibility();
      this.onCountryFilter();
    },
    watch: {
      currentCountry() {
        const tab = this.tabData.find((t) => t.id === 2);
        if (tab)
          tab.hidden = this.integrationIndicatorsHidden || this.currentCountry?.groupId !== Constants.countryGroupEAEC;
      },
      country: function (val: Nullable<string | number>) {
        if (Number(val) === Constants.countryRussiaId && this.activeTabId === 8) {
          this.activeTabId = this.dataTabs[11].id;
          this.activeTab = this.dataTabs[11].text;
        }
        this.$router.replace({
          query: this.country ? { countryId: String(this.country) } : undefined,
        });
        this.loadData();
      },
    },
  });
